import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  DollarSign, 
  Calendar, 
  CheckCircle,
  CreditCard,
  Clock,
  Target,
  AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import StandardizedIcon from '@/components/wizard/StandardizedIcon';
import { supabase } from '@/integrations/supabase/client';

interface BuyRequestFieldsDisplayProps {
  financingStatus?: string;
  downPayment?: number;
  loanTerm?: number;
  preferredDeliveryDate?: string;
  urgencyLevel?: string;
  expiresAt?: string;
  modelPreferences?: string[] | any;
  requiredEquipment?: string[] | any;
  maxAge?: number;
  maxFlightHours?: number;
  budgetMin?: number;
  budgetMax?: number;
  className?: string;
  // New props for buy dry/wet lease
  operationalCoverage?: string[];
  searchRadius?: number;
  bestTimeToCall?: string;
  insuranceCapability?: string;
  pilotQualifications?: string[];
  // Rate options
  hourlyRate?: number;
  dailyRate?: number;
  monthlyRate?: number;
  // New wet lease specific fields
  serviceLevel?: string;
  responseTime?: string;
  operatingHours?: string;
  intendedUse?: string;
  minimumBilling?: string;
  preferredContactMethods?: string[];
  requestedServices?: string[];
  // New buy wizard fields
  budgetFlexibility?: string;
  preApprovalAmount?: number;
  preferredDeliveryMethod?: string;
  tradeInAircraft?: string;
  // Minimum condition
  minCondition?: string;
  configurationName?: string;
}

const BuyRequestFieldsDisplay: React.FC<BuyRequestFieldsDisplayProps> = ({
  financingStatus,
  downPayment,
  loanTerm,
  preferredDeliveryDate,
  urgencyLevel,
  expiresAt,
  modelPreferences,
  requiredEquipment,
  maxAge,
  maxFlightHours,
  budgetMin,
  budgetMax,
  className,
  operationalCoverage,
  searchRadius,
  bestTimeToCall,
  insuranceCapability,
  pilotQualifications,
  hourlyRate,
  dailyRate,
  monthlyRate,
  serviceLevel,
  responseTime,
  operatingHours,
  intendedUse,
  minimumBilling,
  preferredContactMethods,
  requestedServices,
  budgetFlexibility,
  preApprovalAmount,
  preferredDeliveryMethod,
  tradeInAircraft,
  minCondition,
  configurationName
}) => {
  // Format condition labels for buy requests
  const formatConditionLabel = (condition?: string): string => {
    if (!condition) return '';
    const labels: Record<string, string> = {
      'new': 'New',
      'excellent': 'Excellent',
      'good': 'Good or Better',
      'fair': 'Fair or Better',
      'serviceable': 'Serviceable'
    };
    return labels[condition.toLowerCase()] || condition;
  };

  const [resolvedModels, setResolvedModels] = useState<any[]>([]);

  // Calculate days until delivery or expiration
  const getDaysUntil = (dateString?: string) => {
    if (!dateString) return null;
    const days = Math.ceil((new Date(dateString).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const deliveryDays = getDaysUntil(preferredDeliveryDate);
  const expiryDays = getDaysUntil(expiresAt);

  // Parse model preferences and required equipment
  const modelList = Array.isArray(modelPreferences) 
    ? modelPreferences 
    : (modelPreferences && typeof modelPreferences === 'object' ? Object.values(modelPreferences) : []);

  const equipmentList = Array.isArray(requiredEquipment)
    ? requiredEquipment
    : (requiredEquipment && typeof requiredEquipment === 'object' ? Object.values(requiredEquipment) : []);

  // Resolve UUIDs to helicopter names
  useEffect(() => {
    const resolveModelPreferences = async () => {
      if (modelList.length === 0) {
        setResolvedModels([]);
        return;
      }

      // Check if first item is a UUID string
      const firstItem = modelList[0];
      const isUUID = typeof firstItem === 'string' && 
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(firstItem);

      if (isUUID) {
        // Fetch helicopter data for UUIDs
        const uuids = modelList.filter((id: any) => typeof id === 'string');
        const { data, error } = await supabase
          .from('helicopters')
          .select('id, manufacturer, model, icon_url')
          .in('id', uuids);

        if (!error && data) {
          setResolvedModels(data);
        } else {
          // Fallback: show UUIDs truncated
          setResolvedModels(modelList.map((id: string) => ({ 
            id, 
            manufacturer: '', 
            model: `ID: ${id.substring(0, 8)}...` 
          })));
        }
      } else {
        // Already full objects or names
        setResolvedModels(modelList);
      }
    };

    resolveModelPreferences();
  }, [modelPreferences]);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Rate Options - Priority: hourly > daily > monthly */}
      {(hourlyRate || dailyRate || monthlyRate) && (
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 border-emerald-200/60 dark:border-emerald-700/40">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Rate Options</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {hourlyRate && (
                <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                  <div className="text-xs text-muted-foreground">Hourly</div>
                  <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">${hourlyRate.toLocaleString()}/h</div>
                </div>
              )}
              {dailyRate && (
                <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                  <div className="text-xs text-muted-foreground">Daily</div>
                  <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">${dailyRate.toLocaleString()}/day</div>
                </div>
              )}
              {monthlyRate && (
                <div className="text-center p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                  <div className="text-xs text-muted-foreground">Monthly</div>
                  <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">${monthlyRate.toLocaleString()}/mo</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Range - Prominently displayed */}
      {(budgetMin || budgetMax) && (
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">Budget Range</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              ${(budgetMin || 0).toLocaleString()} - ${(budgetMax || 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgency & Timeline Section */}
      {(urgencyLevel || deliveryDays !== null || expiryDays !== null) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {urgencyLevel && (
              <Badge 
                className={cn(
                  "text-white border-0 font-bold shadow-lg animate-badge-bounce",
                  urgencyLevel === 'urgent' && "urgency-urgent",
                  urgencyLevel === 'high' && "urgency-high",
                  urgencyLevel === 'moderate' && "urgency-moderate",
                  !['urgent', 'high', 'moderate'].includes(urgencyLevel) && "bg-blue-500"
                )}
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                {urgencyLevel.toUpperCase()}
              </Badge>
            )}
            
            {deliveryDays !== null && deliveryDays <= 90 && (
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                <Calendar className="w-3 h-3 mr-1" />
                Wants in {deliveryDays} days
              </Badge>
            )}

            {expiryDays !== null && expiryDays <= 7 && (
              <Badge variant="destructive" className="animate-pulse">
                <Clock className="w-3 h-3 mr-1" />
                Expires in {expiryDays} days
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Financing Details */}
      {(financingStatus || downPayment || loanTerm || budgetFlexibility || preApprovalAmount) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {financingStatus && (
             <div className="sm:col-span-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
               <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
               <div className="min-w-0">
                 <div className="text-xs text-green-700 dark:text-green-300 font-medium">Financing</div>
                 <div className="text-xs text-green-600 dark:text-green-400 font-semibold capitalize">
                  {financingStatus.replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          )}

          {budgetFlexibility && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
              <Target className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">Budget Flexibility</div>
                <div className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold capitalize">{budgetFlexibility.replace(/_/g, ' ')}</div>
              </div>
            </div>
          )}

          {preApprovalAmount && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">Pre-Approval</div>
                <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold">${preApprovalAmount.toLocaleString()}</div>
              </div>
            </div>
          )}

          {downPayment && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <DollarSign className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">Down Payment</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">${downPayment.toLocaleString()}</div>
              </div>
            </div>
          )}

          {loanTerm && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <CreditCard className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-purple-700 dark:text-purple-300 font-medium">Loan Term</div>
                <div className="text-xs text-purple-600 dark:text-purple-400 font-semibold">{loanTerm} months</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delivery Details */}
      {(preferredDeliveryMethod || tradeInAircraft) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {preferredDeliveryMethod && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
              <Target className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-sky-700 dark:text-sky-300 font-medium">Delivery Method</div>
                <div className="text-xs text-sky-600 dark:text-sky-400 font-semibold capitalize">{preferredDeliveryMethod.replace(/_/g, ' ')}</div>
              </div>
            </div>
          )}

          {tradeInAircraft && (
            <div className="sm:col-span-2 flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Target className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-amber-700 dark:text-amber-300 font-medium">Trade-in Aircraft</div>
                <div className="text-xs text-amber-600 dark:text-amber-400 font-semibold">{tradeInAircraft}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Requirements Section */}
      {(maxAge || maxFlightHours || minCondition) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {minCondition && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800">
              <CheckCircle className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-teal-700 dark:text-teal-300 font-medium">Min Condition</div>
                <div className="text-xs text-teal-600 dark:text-teal-400 font-semibold">{formatConditionLabel(minCondition)}</div>
              </div>
            </div>
          )}

          {maxAge && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Calendar className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-amber-700 dark:text-amber-300 font-medium">Max Age</div>
                <div className="text-xs text-amber-600 dark:text-amber-400 font-semibold">{maxAge} years</div>
              </div>
            </div>
          )}

          {maxFlightHours && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
              <Clock className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-orange-700 dark:text-orange-300 font-medium">Max Hours</div>
                <div className="text-xs text-orange-600 dark:text-orange-400 font-semibold">{maxFlightHours.toLocaleString()}h</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preferred Configuration */}
      {configurationName && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
            <Target className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-violet-700 dark:text-violet-300 font-medium">Preferred Configuration</div>
              <div className="text-xs text-violet-600 dark:text-violet-400 font-semibold">{configurationName}</div>
          </div>
        </div>
      )}

      {/* Preferred Delivery Date - Explicit Display */}
      {preferredDeliveryDate && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800">
            <Calendar className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-cyan-700 dark:text-cyan-300 font-medium">Preferred Delivery</div>
              <div className="text-xs text-cyan-600 dark:text-cyan-400 font-semibold">
              {new Date(preferredDeliveryDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>
        </div>
      )}

      {/* Model Preferences - Enhanced with icons and UUID resolution */}
      {resolvedModels.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-4 h-4 text-primary" />
              Model Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {resolvedModels.slice(0, 5).map((model: any, idx: number) => {
              const modelData = typeof model === 'object' ? model : { name: model };
              return (
                <div key={idx} className="flex items-center gap-3 p-2 border rounded-lg hover:bg-accent/50 transition-colors">
                  {modelData.icon_url && (
                    <StandardizedIcon 
                      src={modelData.icon_url}
                      alt={modelData.name || modelData.model || 'Helicopter'}
                      size="sm"
                      className="flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      {modelData.manufacturer || ''} {modelData.model || modelData.name || 'Model'}
                    </div>
                    {modelData.year_range && (
                      <div className="text-xs text-muted-foreground">
                        {modelData.year_range}
                      </div>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    #{idx + 1}
                  </Badge>
                </div>
              );
            })}
            {resolvedModels.length > 5 && (
              <p className="text-xs text-muted-foreground text-center pt-1">
                +{resolvedModels.length - 5} more models
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Required Equipment - Enhanced layout */}
      {equipmentList.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="w-4 h-4 text-primary" />
              Required Equipment
              <Badge variant="secondary" className="ml-auto text-xs">
                {equipmentList.length} items
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {equipmentList.map((equipment: any, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800">
                  {typeof equipment === 'object' ? equipment.name : equipment}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coverage Areas */}
      {operationalCoverage && operationalCoverage.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-4 h-4 text-primary" />
              Coverage Areas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {operationalCoverage.map((area: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                  {area}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Radius */}
      {searchRadius && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <Target className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">Service Radius</div>
            <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">{searchRadius} miles</div>
          </div>
        </div>
      )}

      {/* Best Time to Call */}
      {bestTimeToCall && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
          <Clock className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-purple-700 dark:text-purple-300 font-medium">Best Time to Call</div>
            <div className="text-xs text-purple-600 dark:text-purple-400 font-semibold capitalize">{bestTimeToCall}</div>
          </div>
        </div>
      )}

      {/* Service Level & Response Time - Wet Lease specific */}
      {(serviceLevel || responseTime) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-primary" />
              Service Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {serviceLevel && (
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="text-xs text-blue-700 dark:text-blue-300 font-medium">Service Level</div>
                  <div className="text-sm text-blue-600 dark:text-blue-400 font-semibold capitalize">{serviceLevel.replace(/_/g, ' ')}</div>
                </div>
              )}
              {responseTime && (
                <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="text-xs text-purple-700 dark:text-purple-300 font-medium">Response Time</div>
                  <div className="text-sm text-purple-600 dark:text-purple-400 font-semibold capitalize">{responseTime.replace(/_/g, ' ')}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Operational Details - Wet Lease specific */}
      {(operatingHours || intendedUse || minimumBilling) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-primary" />
              Operational Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {operatingHours && (
                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="text-xs text-amber-700 dark:text-amber-300 font-medium">Operating Hours</div>
                  <div className="text-sm text-amber-600 dark:text-amber-400 font-semibold capitalize">{operatingHours.replace(/_/g, ' ')}</div>
                </div>
              )}
              {intendedUse && (
                <div className="p-2 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800">
                  <div className="text-xs text-teal-700 dark:text-teal-300 font-medium">Intended Use</div>
                  <div className="text-sm text-teal-600 dark:text-teal-400 font-semibold capitalize">{intendedUse.replace(/_/g, ' ')}</div>
                </div>
              )}
              {minimumBilling && (
                <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                  <div className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">Minimum Billing</div>
                  <div className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold capitalize">{minimumBilling.replace(/_/g, ' ')}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preferred Contact Methods */}
      {preferredContactMethods && preferredContactMethods.length > 0 && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-700/40">
          <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-green-700 dark:text-green-400 font-medium">Preferred Contact Methods</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {preferredContactMethods.map((method: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700/40">
                  {method}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Requested Services */}
      {requestedServices && requestedServices.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="w-4 h-4 text-primary" />
              Requested Services
              <Badge variant="secondary" className="ml-auto text-xs">
                {requestedServices.length} services
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {requestedServices.map((service: string, idx: number) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800">
                  {service}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insurance & Qualifications */}
      {(insuranceCapability || (pilotQualifications && pilotQualifications.length > 0)) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="w-4 h-4 text-primary" />
              Operational Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {insuranceCapability && (
               <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                 <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                 <div className="min-w-0">
                   <div className="text-xs text-green-700 dark:text-green-300 font-medium">Insurance</div>
                   <div className="text-xs text-green-600 dark:text-green-400 font-semibold">{insuranceCapability}</div>
                </div>
              </div>
            )}
            {pilotQualifications && pilotQualifications.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {pilotQualifications.map((qual: string, idx: number) => (
                  <Badge key={idx} variant="secondary" className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800">
                    {qual}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline Countdown - Enhanced display */}
      {deliveryDays !== null && deliveryDays > 0 && deliveryDays <= 90 && (
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Wants delivery in</div>
                <div className="text-2xl font-bold text-primary">{deliveryDays} days</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BuyRequestFieldsDisplay;